import { useMemo } from "react";
import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";

// ── shared field helpers ────────────────────────────────────────────────────
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const dateOnly = (v: unknown): string =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};

// ────────────────────────────────────────────────────────────────────────────
// Diagnoses  ←  Condition
// ────────────────────────────────────────────────────────────────────────────

export interface DiagnosisRecord {
  id: string;
  name: string;
  icdCode: string; // "" when unknown
  snomedCode: string; // "" when unknown
  diagnosedDate: string; // "" when unknown
  diagnosedBy: string; // "" — Condition carries no practitioner link
  status: "active" | "chronic" | "resolved";
  severity: string; // "" — not part of the FHIR Condition data we store
  description: string; // "" when unknown (falls back to the record's free-text note)
  plainLanguageSummary: string; // "" — no AI summary is generated for conditions yet
  relatedRecords: string[];
  clinicalStatus: string;
  verificationStatus: string;
}

function mapConditionStatus(clinicalStatus: string): DiagnosisRecord["status"] {
  const s = clinicalStatus.toLowerCase();
  if (s.includes("resolv") || s.includes("inactive") || s.includes("remission")) {
    return "resolved";
  }
  return "active";
}

export function adaptDiagnosis(record: ParsedRecord): DiagnosisRecord {
  const f = record.fields;
  const clinicalStatus = str(f.clinical_status);
  return {
    id: record.id,
    name: firstStr(f.display_name) || "Unspecified condition",
    icdCode: str(f.icd10_code),
    snomedCode: str(f.snomed_code),
    diagnosedDate: firstDate(f.onset_date, f.recorded_date, f.asserted_date),
    diagnosedBy: "",
    status: mapConditionStatus(clinicalStatus),
    severity: "",
    description: str(f.note),
    plainLanguageSummary: "",
    relatedRecords: typeof f.encounter_ref === "string" && f.encounter_ref ? [f.encounter_ref] : [],
    clinicalStatus,
    verificationStatus: str(f.verification_status),
  };
}

export interface DiagnosesResult {
  diagnoses: DiagnosisRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDiagnoses(): DiagnosesResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const diagnoses = useMemo(() => {
    return records
      .filter((r) => r.resourceType === "Condition")
      .map(adaptDiagnosis)
      .sort((a, b) => b.diagnosedDate.localeCompare(a.diagnosedDate));
  }, [records]);
  return { diagnoses, isLoading, error, refresh };
}

// ────────────────────────────────────────────────────────────────────────────
// Complete History  ←  Procedure + Condition + AllergyIntolerance / FamilyMemberHistory
// ────────────────────────────────────────────────────────────────────────────

export interface MedicalHistoryRecord {
  id: string;
  type: "surgery" | "hospitalization" | "procedure" | "condition" | "allergy";
  title: string;
  date: string; // "" when unknown
  provider: string; // "" when unknown
  facility: string; // "" when unknown
  notes: string; // "" when unknown
  outcome: string; // "" when unknown
}

export interface FamilyHistoryRecord {
  id: string;
  relation: string;
  relationKey: string; // normalised label used to slot the genogram
  condition: string; // "" when unknown
  ageAtOnset?: number;
  status: "living" | "deceased";
  notes: string; // "" when unknown
  sex: string;
}

function mapProcedureType(display: string, category: string): MedicalHistoryRecord["type"] {
  const hay = `${display} ${category}`.toLowerCase();
  if (hay.includes("surg") || hay.includes("operat") || hay.includes("ectomy") || hay.includes("plasty")) {
    return "surgery";
  }
  if (hay.includes("admission") || hay.includes("inpatient") || hay.includes("hospital")) {
    return "hospitalization";
  }
  return "procedure";
}

export function adaptMedicalHistory(record: ParsedRecord): MedicalHistoryRecord {
  const f = record.fields;
  if (record.resourceType === "Procedure") {
    return {
      id: record.id,
      type: mapProcedureType(str(f.display_name), str(f.category)),
      title: firstStr(f.display_name) || "Procedure",
      date: firstDate(f.performed_date, f.performed_start, f.performed_end),
      provider: "",
      facility: "",
      notes: firstStr(f.reason_display),
      outcome: "",
    };
  }
  if (record.resourceType === "AllergyIntolerance") {
    return {
      id: record.id,
      type: "allergy",
      title: firstStr(f.display_name, f.code) || "Allergy",
      date: firstDate(f.onset_date, f.recorded_date),
      provider: "",
      facility: "",
      notes: firstStr(f.reaction_manifestation, f.reaction_description),
      outcome: firstStr(f.criticality),
    };
  }
  // Condition
  return {
    id: record.id,
    type: "condition",
    title: firstStr(f.display_name) || "Past condition",
    date: firstDate(f.onset_date, f.recorded_date, f.asserted_date),
    provider: "",
    facility: "",
    notes: str(f.note),
    outcome: firstStr(f.clinical_status),
  };
}

const RELATION_SLOTS = [
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Paternal Grandfather",
  "Paternal Grandmother",
  "Maternal Grandfather",
  "Maternal Grandmother",
];

function normaliseRelation(raw: string): string {
  const s = raw.trim().toLowerCase();
  const match = RELATION_SLOTS.find((slot) => slot.toLowerCase() === s);
  if (match) return match;
  if (s === "dad") return "Father";
  if (s === "mom" || s === "mum") return "Mother";
  if (s === "grandfather" || s === "grandpa") return "Paternal Grandfather";
  if (s === "grandmother" || s === "grandma") return "Maternal Grandmother";
  // Title-case whatever the vendor sent so it still renders sensibly.
  return raw
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function adaptFamilyHistory(record: ParsedRecord): FamilyHistoryRecord {
  const f = record.fields;
  const relationRaw = firstStr(f.relationship_display, f.relationship_code) || "Family member";
  const ageRaw = str(f.condition_onset_age).match(/\d+/);
  return {
    id: record.id,
    relation: relationRaw,
    relationKey: normaliseRelation(relationRaw),
    condition: str(f.condition_display),
    ageAtOnset: ageRaw ? Number(ageRaw[0]) : undefined,
    status: f.deceased === true ? "deceased" : "living",
    notes: str(f.note),
    sex: str(f.sex),
  };
}

export interface ClinicalHistoryResult {
  medicalHistory: MedicalHistoryRecord[];
  familyHistory: FamilyHistoryRecord[];
  /** Always empty — social history is not extracted by the pipeline yet. */
  socialHistory: never[];
  hasSocialSource: false;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useClinicalHistory(): ClinicalHistoryResult {
  const { records, isLoading, error, refresh } = useParsedRecords();

  const medicalHistory = useMemo(() => {
    return records
      .filter(
        (r) =>
          r.resourceType === "Procedure" ||
          r.resourceType === "Condition" ||
          r.resourceType === "AllergyIntolerance",
      )
      .map(adaptMedicalHistory)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [records]);

  const familyHistory = useMemo(() => {
    return records
      .filter((r) => r.resourceType === "FamilyMemberHistory")
      .map(adaptFamilyHistory);
  }, [records]);

  return {
    medicalHistory,
    familyHistory,
    socialHistory: [],
    hasSocialSource: false,
    isLoading,
    error,
    refresh,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Health Concerns  ←  Condition (provider-recorded); patient-reported has no source
// ────────────────────────────────────────────────────────────────────────────

export interface HealthConcernRecord {
  id: string;
  concern: string;
  category: "symptom" | "condition" | "preventive" | "lifestyle" | "mental_health";
  severity: "mild" | "moderate" | "severe" | "unknown";
  status: "active" | "monitoring" | "resolved";
  onsetDate: string; // "" when unknown
  lastUpdated: string; // "" when unknown
  notes: string; // "" when unknown
  icdCode: string;
  source: "provider";
}

function mapConcernStatus(clinicalStatus: string): HealthConcernRecord["status"] {
  const s = clinicalStatus.toLowerCase();
  if (s.includes("resolv") || s.includes("inactive") || s.includes("remission")) return "resolved";
  if (s.includes("recurrence") || s.includes("relapse")) return "monitoring";
  return "active";
}

export function adaptHealthConcern(record: ParsedRecord): HealthConcernRecord {
  const f = record.fields;
  return {
    id: record.id,
    concern: firstStr(f.display_name) || "Health concern",
    category: "condition",
    severity: "unknown",
    status: mapConcernStatus(str(f.clinical_status)),
    onsetDate: firstDate(f.onset_date, f.recorded_date, f.asserted_date),
    lastUpdated: dateOnly(record.updatedAt),
    notes: str(f.note),
    icdCode: str(f.icd10_code),
    source: "provider",
  };
}

export interface HealthConcernsResult {
  concerns: HealthConcernRecord[];
  /** Always empty — patient-reported concerns need a submit/list endpoint that does not exist yet. */
  patientConcerns: never[];
  hasPatientReportedSource: false;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useHealthConcerns(): HealthConcernsResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  const concerns = useMemo(() => {
    return records
      .filter((r) => r.resourceType === "Condition")
      .map(adaptHealthConcern)
      .sort((a, b) => b.onsetDate.localeCompare(a.onsetDate));
  }, [records]);
  return {
    concerns,
    patientConcerns: [],
    hasPatientReportedSource: false,
    isLoading,
    error,
    refresh,
  };
}
